// Replays (ADR 0014): the container packed by the ride session is compressed here (the browser's native gzip), cached
// in IndexedDB and sent to the server with its activity. An upload that fails while the server is unreachable waits
// in the outbox (`synced: false`); sync.ts flushes it with the activities.
import { unpackReplay, type Replay } from '@gruvetto/core';
import { db } from './db.js';
import { apiBytes, ApiError } from './api.js';

interface ReplayRow { id: string; data: Uint8Array; synced: boolean }

async function pipe(bytes: Uint8Array, stream: CompressionStream | DecompressionStream): Promise<Uint8Array> {
  return new Uint8Array(await new Response(new Blob([bytes as BlobPart]).stream().pipeThrough(stream as ReadableWritablePair<Uint8Array, Uint8Array>)).arrayBuffer());
}
export const gzip = (bytes: Uint8Array) => pipe(bytes, new CompressionStream('gzip'));
export const gunzip = (bytes: Uint8Array) => pipe(bytes, new DecompressionStream('gzip'));

/** The cached activity learns it has a replay (the server's list says so too, once uploaded). */
async function markActivity(id: string) {
  const a = await db.get<{ id: string }>('activities', id);
  if (a) await db.put('activities', { ...a, replay: true });
}

async function upload(id: string, data: Uint8Array) {
  await apiBytes(`/activities/${encodeURIComponent(id)}/replay`, { method: 'PUT', body: data });
  await db.put('replays', { id, data, synced: true } satisfies ReplayRow);
  await markActivity(id);
}

/** Keeps the replay of a saved activity: cached at once, uploaded now or at the next sync. */
export async function keepReplay(activityId: string, packed: Uint8Array): Promise<void> {
  const data = await gzip(packed);
  await db.put('replays', { id: activityId, data, synced: false } satisfies ReplayRow);
  await markActivity(activityId);
  try { await upload(activityId, data); } catch (e) { if (!(e instanceof ApiError && e.offline)) throw e; }
}

/** Sends the replays left in the outbox (after their activities, which sync.ts sends first). */
export async function flushReplays(): Promise<void> {
  for (const r of (await db.all<ReplayRow>('replays')).filter(x => !x.synced)) {
    try { await upload(r.id, r.data); } catch (e) {
      // the activity never made it to the server: its replay has nowhere to go
      if (e instanceof ApiError && e.status === 404) await db.del('replays', r.id); else throw e;
    }
  }
}

/** The replay of an activity, from the cache or the server; null when there is none. */
export async function loadReplay(activityId: string): Promise<Replay | null> {
  const row = await db.get<ReplayRow>('replays', activityId);
  if (row) return unpackReplay(await gunzip(row.data));
  try {
    // served gzip-encoded: the browser inflates it on the way in
    const packed = new Uint8Array(await apiBytes(`/activities/${encodeURIComponent(activityId)}/replay`));
    const replay = unpackReplay(packed);
    await db.put('replays', { id: activityId, data: await gzip(packed), synced: true } satisfies ReplayRow);
    return replay;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}
